import { useCallback, useMemo, useState } from 'react';

import { UseRecordingsHookReturnType } from 'utils/hooks/useRecordings';

import { ManageRecordingItem } from 'screens/Detail/types';

type Props = {
    isRecordingLoading: UseRecordingsHookReturnType['isRecordingLoading'];
    recordingsButtonsList: UseRecordingsHookReturnType['recordingsButtonsList'];
    hasGlobalRecordingPermission: UseRecordingsHookReturnType['hasGlobalRecordingPermission'];
    onManageRecordingPressCallback: (item: ManageRecordingItem) => void;
};

const useRecordingActionsDialog = ({
    isRecordingLoading,
    recordingsButtonsList,
    hasGlobalRecordingPermission,
    onManageRecordingPressCallback,
}: Props) => {
    const [recordingActionsDialogVisible, setRecordingActionsDialogVisible] =
        useState(false);

    const showRecordingActionsButton =
        hasGlobalRecordingPermission && !!recordingsButtonsList?.length;

    const [recordingActionsButtonPosition, setRecordingActionsButtonPosition] =
        useState<
            undefined | { x: number; y: number; width: number; height: number }
        >();

    const handleRecordingActionsButtonPress = useCallback(
        (buttonPosition: {
            x: number;
            y: number;
            width: number;
            height: number;
        }) => {
            if (!isRecordingLoading) {
                setRecordingActionsButtonPosition(buttonPosition);
                setRecordingActionsDialogVisible(true);
            }
        },
        [isRecordingLoading],
    );

    const handleCloseRecordingActionsDialog = useCallback(() => {
        setRecordingActionsDialogVisible(false);
    }, []);

    const onRecordingItemPress = useCallback(
        (item: ManageRecordingItem) => {
            handleCloseRecordingActionsDialog();
            onManageRecordingPressCallback(item);
        },
        [handleCloseRecordingActionsDialog, onManageRecordingPressCallback],
    );

    return useMemo(
        () => ({
            showRecordingActionsButton,
            recordingActionsButtonPosition,
            recordingActionsDialogVisible,
            setRecordingActionsDialogVisible,
            handleRecordingActionsButtonPress,
            recordingsButtonsList,
            onRecordingItemPress,
            handleCloseRecordingActionsDialog,
        }),
        [
            showRecordingActionsButton,
            recordingActionsButtonPosition,
            recordingActionsDialogVisible,
            handleRecordingActionsButtonPress,
            recordingsButtonsList,
            onRecordingItemPress,
            handleCloseRecordingActionsDialog,
        ],
    );
};

export default useRecordingActionsDialog;
